import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import ReadOnlyBadge from '@shared/components/ReadOnlyBadge';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { UserPlus, Mail, Shield, Trash2, Edit2, Check, X } from 'lucide-react';
import { getRoleTone } from '@shared/config/theme';
import { StatusChip } from '@shared/components/StatusChip';

interface Invitation {
  id: string;
  organization_id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
}

interface Member {
  id: string;
  organization_id: string;
  user_id: string;
  role: string;
  email: string;
  full_name: string;
  joined_at: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface CustomRole {
  id: string;
  organization_id: string;
  name: string;
}

export default function TeamMembers() {
  const { t, i18n } = useTranslation('settings');
  const { showConfirm } = useDialog();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<string>('');

  const token = getToken();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      void loadSelectedOrganization();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    setLoadingData(true);
    setLoadError(false);
    try {
      const res = await fetch('/api/v1/organizations', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Organization list request failed');
      const data = await res.json();
      setOrganizations(data.organizations || []);
      if (data.organizations?.length > 0 && !selectedOrg) {
        setSelectedOrg(data.organizations[0].id);
      } else if (!data.organizations?.length) {
        setLoadingData(false);
      }
    } catch {
      setLoadError(true);
      setLoadingData(false);
    }
  };

  const fetchInvitations = async () => {
    const res = await fetch(`/api/v1/invitations?organization_id=${selectedOrg}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Invitation list request failed');
    setInvitations((await res.json()).invitations || []);
  };

  const fetchMembers = async () => {
    const res = await fetch(`/api/v1/members?organization_id=${selectedOrg}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Member list request failed');
    setMembers((await res.json()).members || []);
  };

  const fetchCustomRoles = async () => {
    const res = await fetch(`/api/v1/custom-roles?organization_id=${selectedOrg}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Custom role list request failed');
    setCustomRoles((await res.json()) || []);
  };

  const loadSelectedOrganization = async () => {
    setLoadingData(true);
    setLoadError(false);
    try {
      await Promise.all([fetchInvitations(), fetchMembers(), fetchCustomRoles()]);
    } catch {
      setLoadError(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch('/api/v1/invitations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          organization_id: selectedOrg,
          email: inviteEmail,
          role: inviteRole,
        }),
      });

      if (res.ok) {
        setMessage({
          type: 'success',
          text: t('teamMembers.messages.invited', { email: inviteEmail }),
        });
        setInviteEmail('');
        setShowInviteForm(false);
        fetchInvitations();
      } else throw new Error(t('teamMembers.invitationFailed'));
    } catch {
      setMessage({ type: 'error', text: t('teamMembers.invitationFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (memberId: string) => {
    try {
      const res = await fetch(`/api/v1/members/update-role?member_id=${memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        setEditingMember(null);
        fetchMembers();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRemove = async (memberId: string, memberName: string) => {
    const confirmed = await showConfirm(
      t('teamMembers.confirmRemove', { name: memberName }),
      t('teamMembers.removeMember', { name: memberName }),
      'warning'
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`/api/v1/members/remove?member_id=${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchMembers();
    } catch (e) {
      console.error(e);
    }
  };

  const selectedOrgData = organizations.find((o) => o.id === selectedOrg);
  const organizationRole = selectedOrgData?.role.toUpperCase();
  const canManage = organizationRole === 'OWNER' || organizationRole === 'ADMIN';
  const pendingInvitations = invitations.filter((inv) =>
    ['pending', 'sent'].includes(inv.status.toLowerCase())
  );
  const translateRole = (role: string) =>
    t(`team.roles.${role.toLowerCase()}`, { defaultValue: role });
  const translateInvitationStatus = (status: string) =>
    t(`team.invitationStatuses.${status.toLowerCase()}`, { defaultValue: status });
  const formatDate = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? t('teamMembers.unknownDate')
      : date.toLocaleDateString(i18n.language);
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
                {t('teamMembers.title')}
              </h1>
              <ReadOnlyBadge />
            </div>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('teamMembers.subtitle')}
            </p>
          </div>

          {canManage && (
            <Button
              onClick={() => setShowInviteForm(true)}
              className="min-h-11 w-full md:w-auto shadow-brand-200"
            >
              <UserPlus aria-hidden="true" className="w-5 h-5 mr-2" strokeWidth={3} />{' '}
              {t('teamMembers.inviteButton')}
            </Button>
          )}
        </div>

        {organizations.length > 0 && (
          <Card className="p-4 flex items-center gap-4 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark">
            <Shield className="text-text-subtle-light dark:text-text-subtle-dark" />
            <div className="flex-1">
              <label
                htmlFor="team-organization"
                className="text-xs font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-widest"
              >
                {t('teamMembers.organization')}
              </label>
              <select
                id="team-organization"
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                className="min-h-11 w-full bg-transparent font-bold text-text-primary-light dark:text-text-primary-dark text-base focus:outline-none cursor-pointer"
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          </Card>
        )}

        {message && (
          <div
            role={message.type === 'error' ? 'alert' : 'status'}
            aria-live={message.type === 'error' ? 'assertive' : 'polite'}
            className={`p-4 rounded-xl border font-bold text-sm ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {message.text}
          </div>
        )}

        {loadError && (
          <div role="alert">
            <Card className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-state-danger-light dark:text-state-danger-dark">
                {t('teamMembers.errors.loadFailed')}
              </p>
              <Button
                onClick={() =>
                  organizations.length ? loadSelectedOrganization() : fetchOrganizations()
                }
                className="min-h-11 w-full sm:w-auto"
              >
                {t('teamMembers.retry')}
              </Button>
            </Card>
          </div>
        )}

        {/* Member List */}
        {loadingData ? (
          <div role="status" aria-live="polite">
            <Card className="p-8 text-center text-text-muted-light dark:text-text-muted-dark">
              {t('teamMembers.loading')}
            </Card>
          </div>
        ) : (
          !loadError && (
            <Card className="overflow-hidden">
              <div className="px-6 py-4 border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
                <h3 className="font-bold text-slate-700 dark:text-slate-200">
                  {t('teamMembers.activeMembers')} ({members.length})
                </h3>
              </div>
              <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="p-4 sm:p-5 lg:p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 group hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
                  >
                    <div className="min-w-0 flex items-center gap-3 sm:gap-4">
                      <div
                        aria-hidden="true"
                        className="w-10 h-10 rounded-full bg-accent-soft-light dark:bg-accent-soft-dark text-brand-700 flex items-center justify-center font-bold text-lg shrink-0"
                      >
                        {member.full_name.charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-text-primary-light dark:text-text-primary-dark break-words">
                          {member.full_name}
                        </p>
                        <p className="text-sm text-text-muted-light dark:text-text-muted-dark break-all">
                          {member.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex w-full items-center justify-between gap-3 lg:w-auto lg:justify-end lg:gap-4">
                      {editingMember === member.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            aria-label={t('teamMembers.editRole', { name: member.full_name })}
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            className="min-h-11 min-w-28 rounded-xl border border-surface-border-light dark:border-surface-border-dark bg-surface-card-light dark:bg-surface-card-dark px-3 text-sm font-medium text-text-primary-light dark:text-text-primary-dark"
                          >
                            {['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'].map((r) => (
                              <option key={r} value={r}>
                                {translateRole(r)}
                              </option>
                            ))}
                            {customRoles.map((r) => (
                              <option key={r.id} value={r.name}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            className="min-h-11 min-w-11"
                            aria-label={t('teamMembers.saveRole', { name: member.full_name })}
                            onClick={() => handleRoleUpdate(member.id)}
                          >
                            <Check aria-hidden="true" size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="min-h-11 min-w-11"
                            aria-label={t('teamMembers.cancelRole', { name: member.full_name })}
                            onClick={() => setEditingMember(null)}
                          >
                            <X aria-hidden="true" size={14} />
                          </Button>
                        </div>
                      ) : (
                        <StatusChip
                          tone={getRoleTone(member.role)}
                          label={translateRole(member.role)}
                        />
                      )}

                      {canManage && editingMember !== member.id && (
                        <div className="flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="min-h-11 min-w-11"
                            aria-label={t('teamMembers.editRole', { name: member.full_name })}
                            onClick={() => {
                              setEditingMember(member.id);
                              setNewRole(member.role.toUpperCase());
                            }}
                          >
                            <Edit2
                              size={16}
                              className="text-state-info-light dark:text-state-info-dark"
                            />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="min-h-11 min-w-11"
                            aria-label={t('teamMembers.removeAction', { name: member.full_name })}
                            onClick={() => handleRemove(member.id, member.full_name)}
                          >
                            <Trash2
                              size={16}
                              className="text-state-danger-light dark:text-state-danger-dark"
                            />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )
        )}

        {/* Invitations */}
        {pendingInvitations.length > 0 && (
          <Card className="overflow-hidden">
            <div className="px-6 py-4 border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
              <h3 className="font-bold text-slate-700 dark:text-slate-200">
                {t('teamMembers.pendingInvitations')} ({pendingInvitations.length})
              </h3>
            </div>
            <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
              {pendingInvitations.map((inv) => (
                <div key={inv.id} className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark">
                      <Mail size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">{inv.email}</p>
                      <div className="flex items-center gap-2 text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                        <Badge variant="warning" className="text-[10px]">
                          {translateInvitationStatus(inv.status)}
                        </Badge>
                        <span>
                          {t('teamMembers.expires')} {formatDate(inv.expires_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <StatusChip tone={getRoleTone(inv.role)} label={translateRole(inv.role)} />
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Invite Modal */}
        {showInviteForm && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-invite-title"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
          >
            <Card className="w-full max-w-md shadow-elevated">
              <div className="p-8 space-y-6">
                <div className="flex justify-between items-center">
                  <h2
                    id="team-invite-title"
                    className="text-xl sm:text-2xl font-bold text-text-primary-light dark:text-text-primary-dark"
                  >
                    {t('teamMembers.inviteModal.title')}
                  </h2>
                  <button
                    type="button"
                    aria-label={t('common:actions.close')}
                    onClick={() => setShowInviteForm(false)}
                    className="min-h-11 min-w-11 rounded-lg flex items-center justify-center hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                  >
                    <X
                      aria-hidden="true"
                      className="text-text-subtle-light dark:text-text-subtle-dark"
                    />
                  </button>
                </div>

                <form onSubmit={handleInvite} className="space-y-4">
                  <Input
                    id="team-invite-email"
                    label={t('teamMembers.inviteModal.emailLabel')}
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder={t('teamMembers.inviteModal.emailPlaceholder')}
                    required
                  />

                  <div>
                    <label
                      htmlFor="team-invite-role"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('teamMembers.inviteModal.roleLabel')}
                    </label>
                    <select
                      id="team-invite-role"
                      className="min-h-11 w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold text-text-primary-light dark:text-text-primary-dark focus:ring-2 focus:ring-brand-500 outline-none"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                    >
                      {['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'].map((role) => (
                        <option key={role} value={role}>
                          {translateRole(role)}
                        </option>
                      ))}
                      {customRoles.map((r) => (
                        <option key={r.id} value={r.name}>
                          {r.name} ({t('teamMembers.inviteModal.custom')})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="secondary"
                      className="flex-1"
                      onClick={() => setShowInviteForm(false)}
                      type="button"
                    >
                      {t('teamMembers.inviteModal.cancel')}
                    </Button>
                    <Button className="flex-1" type="submit" isLoading={loading}>
                      {t('teamMembers.inviteModal.send')}
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
